import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { CssRoles } from '@common/constants';
import { MaintenanceService } from '@common/ui/maintenance-mode';
import { AppConfigService, GoogleAnalyticsService, SessionMonitor } from '@common/ui/shared-components';
import { Utilities } from '@common/utils';
import { HALF_HOUR_SEC, ONE_MIN_SEC } from '@fscd-intake/entities';
import { FscdNavigation, HealthCheckService, PageNavigation } from '@fscd-intake/ui/application';
import { AuthenticationMethods, AuthenticationService } from '@govalta-emu/keycloak-auth-service';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, combineLatest, lastValueFrom, map, take } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from '../environments/environment';

@Component({
  standalone: false,
  selector: 'fi-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  _destroy$ = new Subject<void>();
  applicationVersion = environment?.appVersion ?? '';
  loading = false;
  applicationComponent;
  copyrightYear = new Date().getFullYear();
  isApplicationPage = false;
  isMobile = false;
  userNavigation = FscdNavigation;
  fscdTestRole = CssRoles.FSCDITester;
  keycloakIdpName = AuthenticationMethods.keycloak.code;
  albertaCaAccountUrl = '';

  @ViewChild('contentSection', { read: ElementRef, static: false })
  contentSection;

  applicationVersionUI$: Observable<string>;
  applicationVersionAPI$: Observable<string>;
  applicationVersionFile$: Observable<string>;
  applicationVersion$: Observable<string>;
  isProd = false;
  prodEnvironment = false;
  environmentName = '';

  notificationMessage = '';
  notificationType = '';
  notificationStartDate?: Date;
  notificationEndDate?: Date;
  maintenanceBannerActive = false;

  constructor(
    private router: Router,
    private renderer: Renderer2,
    private sessionMonitor: SessionMonitor,
    private authenticationService: AuthenticationService,
    private maintenanceService: MaintenanceService,
    private healthCheckService: HealthCheckService,
    private toasterService: ToastrService,
    private cdr: ChangeDetectorRef,
    private configService: AppConfigService,
    private analyticsService: GoogleAnalyticsService
  ) {}

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this.sessionMonitor.stopMonitor();
  }

  async ngOnInit() {
    this.updateIsMobile();

    //"Back to homepage" link is shown on /application and /view-application routes
    this.updateIsApplicationPage(this.router.url);
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this._destroy$)
      )
      .subscribe((nav: NavigationEnd) => this.updateIsApplicationPage(nav.urlAfterRedirects));

    await this.analyticsService.init();

    try {
      const scheduledMaintenance = await this.maintenanceService.scheduledMaintenance();
      const currentRoute = this.router.url.split('?')[0];
      if (!scheduledMaintenance.active && currentRoute === '/serviceError') {
        this.router.navigate(['']);
      }
      if (scheduledMaintenance.active) {
        this.maintenanceBannerActive = true;
      }

      const healthy = await lastValueFrom(this.healthCheckService.isServicesAlive().pipe(take(1)));
      if (!healthy) {
        this.router.navigate(['serviceError']);
        return;
      }
    } catch (error) {
      this.router.navigate(['serviceError']);
      return;
    }

    this.router.events.pipe(takeUntil(this._destroy$)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.loading = true;
        //reset css classes on banner
        this.renderer.removeClass(this.contentSection.nativeElement, 'offset');
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.loading = false;
      }
    });

    this.configService.loadConfig().then(() => {
      // Configuration loaded, you can now access the values
      const config = this.configService.getConfig();
      this.environmentName = (config.ENVIRONMENT_NAME ?? '').toUpperCase();
      //check if prod
      this.prodEnvironment =
        this.environmentName === 'PROD' || this.environmentName === 'PRODUCTION' || !config.ENVIRONMENT_NAME;
      this.notificationMessage = config.NOTIFICATION_BANNER_MESSAGE;
      this.notificationType = config.NOTIFICATION_BANNER_TYPE;
      //load start/end date
      const arrayDates = config.NOTIFICATION_BANNER_DATERANGE as string[];
      if (arrayDates?.length === 2) {
        const startdate = arrayDates[0];
        const enddate = arrayDates[1];
        if (startdate) {
          this.notificationStartDate = new Date(startdate);
        }
        if (enddate) {
          this.notificationEndDate = new Date(enddate);
        }
      }
    });

    let notified = false;
    if (this.authenticationService.isAuthenticated()) {
      this.setVersionObservables();
      await this.authenticationService.refreshToken(HALF_HOUR_SEC);
      this.sessionMonitor.monitorSession(30);
      this.sessionMonitor.sessionTimeRemainingSeconds$
        .pipe(takeUntil(this._destroy$))
        .subscribe((timeRemainingSeconds) => {
          if (timeRemainingSeconds <= ONE_MIN_SEC && timeRemainingSeconds > 0 && !notified) {
            notified = true;
            this.toasterService.warning(
              `Your session will expire shortly. Please save your application to avoid losing information.`,
              undefined,
              {
                disableTimeOut: false,
                timeOut: 15000,
              }
            );
          }

          if (timeRemainingSeconds <= 0) {
            this.timeoutRedirect();
          }
        });
    }
  }

  onActivate(componentRef: unknown) {
    this.applicationComponent = componentRef;
    this.cdr.detectChanges();
  }

  @HostListener('window:resize')
  updateIsMobile() {
    this.isMobile = window.innerWidth < 960;
  }

  async goBackToDashboard() {
    await this.saveCurrentApplication();
    const isSubmitted = this.applicationComponent.application.isSubmitted;
    if (isSubmitted) {
      this.router.navigateByUrl('/dash?tab=submitted');
    } else {
      this.router.navigateByUrl('/dash?tab=draft');
    }
  }

  _version = '';
  get appVersionNumber() {
    if (this._version) {
      return this._version; //return version number once it's set
    }
    if (!this.prodEnvironment && this.applicationVersion && this.environmentName) {
      this._version = `v${this.applicationVersion} (${this.environmentName})`;

      //version number injection hack. remove once design systems fixes version in micro header
      Utilities.ensureVersionNumber(this._version);
    }
    return this._version;
  }

  private timeoutRedirect() {
    const redirectUrl = `${window.location.origin}/session-expired`;
    this.authenticationService.signOut(redirectUrl);
  }

  private updateIsApplicationPage(url: string) {
    const firstSegment = url?.split('?')[0].split('/')[1];
    this.isApplicationPage = firstSegment ? firstSegment.includes('application') : false;
  }

  private async saveCurrentApplication() {
    let valid = true;

    const component = this.applicationComponent?.routerOutlet?.component as PageNavigation;
    if (component && component.next) {
      valid = component.next();
      if (!this.applicationComponent?.doSave || !valid) {
        return;
      }
      await this.applicationComponent.doSave();
    }
  }

  private getVersionInfoUI$(): Observable<string> | null {
    return this.healthCheckService.queryGetUIVersion();
  }

  private getVersionInfoAPI$(): Observable<string> | null {
    return this.healthCheckService.queryGetAPIVersion();
  }

  private getVersionInfoFile$(): Observable<string> | null {
    return this.healthCheckService.queryGetFileVersion();
  }

  private setVersionObservables() {
    const envName = this.configService.getConfig().ENVIRONMENT_NAME?.toLowerCase();
    this.isProd = envName && (envName === 'production' || envName === 'prod');
    //do not do the work of querying this on prod
    if (!this.isProd) {
      this.applicationVersionUI$ = this.getVersionInfoUI$();
      this.applicationVersionAPI$ = this.getVersionInfoAPI$();
      this.applicationVersionFile$ = this.getVersionInfoFile$();
      this.applicationVersion$ = combineLatest([
        this.applicationVersionUI$,
        this.applicationVersionAPI$,
        this.applicationVersionFile$,
      ]).pipe(
        map(([ui, api, file]) => {
          if (!ui && !api && !file) return null;
          return ` | UI: ${ui} | API: ${api} | File: ${file}`;
        })
      );
    }
  }
}
